import React, { FC, ReactElement, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { BasicContainer, FlexCol, FlexDynamic } from '@minecraft.atoms';

const StyledCarouselWrapper = styled(BasicContainer)<{
  dotSize?: number;
  dotBorderColor?: string;
  dotActiveColor?: string;
}>`
  .slick-dots {
    bottom: -10px;

    li {
      button:before {
        width: ${(props) => `${props.dotSize}px`};
        height: ${(props) => `${props.dotSize}px`};
        border: ${(props) => `1px solid ${props.theme.colors.primary[props.dotBorderColor]}`};
        color: transparent;
        opacity: 1;
        margin: 0 3px;
        border-radius: 50%;
      }

      &.slick-active button:before {
        background: ${(props) => props.theme.colors.primary[props.dotActiveColor]};
        opacity: 1;
      }
    }
  }

  .slick-prev,
  .slick-next {
    &:before {
      color: ${(props) => props.theme.colors.primary[props.dotBorderColor]};
      font-size: 24px;
    }
  }

  .slick-prev {
    left: -40px;
  }

  .slick-next {
    right: -40px;
  }

  .slick-disabled {
    opacity: 0.5;
  }
`;

export type CarouselSettings = {
  /** Enable autoplay */
  autoplay?: boolean;
  /** Autoplay speed in milliseconds */
  autoplaySpeed?: number;
  /** Initial slide index */
  initialSlide?: number;
  /** Show dot indicators */
  dots?: boolean;
  /** Enable infinite scrolling */
  infinite?: boolean;
  /** Animation speed in milliseconds */
  speed?: number;
  /** Number of slides to show at once */
  slidesToShow?: number;
  /** Number of slides to scroll per navigation */
  slidesToScroll?: number;
  /** Show navigation arrows */
  arrows?: boolean;
  /** Animation easing function */
  easing?: string;
  /** Custom indicator component */
  customIndicator?: (isActive: boolean) => React.ReactNode;
  /** Custom indicator component */
  customPaging?: (index: number) => ReactElement;
  /** Callback function when slide changes */
  afterChange?: (index: number) => void;
  /** Callback function when slide changes */
  updateCurrentSlide?: (index: number) => void;
  /** Custom previous arrow element */
  prevArrow?: ReactElement;
  /** Custom next arrow element */
  nextArrow?: ReactElement;
  /** Current slide index */
  currentSlide?: number;
  /** CSS easing function */
  cssEase?: string;
  /** Pause on hover */
  pauseOnHover?: boolean;
  /** Pause on focus */
  pauseOnFocus?: boolean;
  /** Responsive settings */
  responsive?: {
    breakpoint: number;
    settings: CarouselSettings;
  }[];
};

export type Props = {
  className?: string;
  /** Carousel settings that control behavior */
  settings?: CarouselSettings;
  children: React.ReactNode[];
  dotSize?: number;
  dotSpacing?: number;
  dotBorderColor?: 'black' | 'primary' | 'default';
  dotActiveColor?: 'black' | 'primary' | 'default';
  'data-testid'?: string;
  'data-qa-id'?: string;
  maxW?: 'md' | 'full';
};

const defaultSettings: CarouselSettings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
  easing: 'linear',
};

/**
 * Carousel component with custom arrows and indicators using react-slick.
 *
 * @example
 * ```tsx
 * <Carousel
 *   className="cn_carousel"
 *   settings={{
 *     dots: false,
 *     prevArrow: <Icon name="carousel-left" />,
 *     nextArrow: <Icon name="carousel-right" />,
 *     customIndicator: (isActive) => (
 *       isActive ? <Icon name="star-filled" /> : <Icon name="star-outlined" />
 *     ),
 *   }}
 * >
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 *   <div>Slide 3</div>
 * </Carousel>
 * ```
 */

export const Carousel: FC<Props> = React.memo(
  ({
    className,
    settings = defaultSettings,
    children,
    dotSize = 10,
    dotBorderColor = 'black',
    dotActiveColor = 'black',
    'data-testid': testId = 'carousel-wrapper',
    'data-qa-id': qaId = '',
    maxW = 'md',
  }) => {
    const { currentSlide, updateCurrentSlide, customIndicator } = settings;
    const [selectedSlide, setSelectedSlide] = useState<number>(Number(currentSlide || 0));
    const sliderRef = useRef<Slider>(null);

    const handleSlideChange = (index: number) => {
      setSelectedSlide(index);
      updateCurrentSlide?.(index);
    };

    const slickSettings = useMemo(() => {
      const computedSettings: CarouselSettings = {
        ...defaultSettings,
        ...settings,
        initialSlide: selectedSlide,
      };

      if (customIndicator) {
        computedSettings.customPaging = (index: number) => {
          const element = customIndicator(selectedSlide === index);
          return React.isValidElement(element) ? element : <>{element}</>;
        };
      }

      return computedSettings;
    }, [customIndicator, selectedSlide, settings]);

    if (children.length === 0) {
      return null;
    }

    return (
      <FlexDynamic
        className={`${className} cn_relative`}
        w="full"
        justifyContent="center"
        alignItems="center"
        gap="3"
        wrap="nowrap"
        mdProps={{
          gap: '8',
        }}
        data-testid={testId}
        data-qa-id={qaId}
      >
        <FlexCol w="full" maxW={maxW} justifyContent="center" alignItems="center" wrap="nowrap">
          <StyledCarouselWrapper
            dotSize={dotSize}
            dotBorderColor={dotBorderColor}
            dotActiveColor={dotActiveColor}
            w="full"
          >
            <Slider
              ref={sliderRef}
              {...slickSettings}
              data-testid={`${testId}-container`}
              afterChange={handleSlideChange}
            >
              {children}
            </Slider>
          </StyledCarouselWrapper>
        </FlexCol>
      </FlexDynamic>
    );
  }
);
